import { SessionContext } from "@/context/SessionContext";
import { Project } from "@/javascript/project-objects";
import { Utils } from "@/javascript/utils";
import {
    Badge,
    Button,
    Card,
    ScrollArea,
    Skeleton,
    Table,
    Text,
    Title
} from "@mantine/core";
import { useContext, useEffect, useState } from "react";

const ClassroomProjectsTable: React.FC = () => {
    const [projects, setProjects] = useState<Project[] | null | undefined>(undefined);

    const session = useContext(SessionContext);
    if (session === undefined) {
        throw new Error("This component must be a child of SessionContext.");
    }

    useEffect(() => {
        setProjects(session.administratingClassroom === null ? null : session.administratingClassroom?.getProjects());
    }, [session.administratingClassroom]);

    return (
        <Card
            h="40vh"
            w="90%"
        >
            <Title>Classroom Projects</Title>
            {projects === undefined ? (
                <Skeleton height={"100%"} radius="md" />
            ) : projects === null ? (
                <Text>No projects found.</Text>
            ) : (
                <ScrollArea
                    style={{
                        width: "100%"
                    }}
                >
                    <Table
                        striped
                        stickyHeader
                        highlightOnHover
                    >
                        <Table.Thead>
                            <Table.Tr>
                                <Table.Th>Name</Table.Th>
                                <Table.Th>Creator</Table.Th>
                                <Table.Th>Created</Table.Th>
                                <Table.Th>Last Edited</Table.Th>
                                <Table.Th>Submitted</Table.Th>
                                <Table.Th>Actions</Table.Th>
                            </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                            {projects.map((project) => (
                                <Table.Tr
                                    key={project.getId()}
                                >
                                    <Table.Td>{project.getName()}</Table.Td>
                                    <Table.Td>{project.getUser()?.username ?? ""}</Table.Td>
                                    <Table.Td><Badge color="blue">{formatDateString(project.getCreatedDate())}</Badge></Table.Td>
                                    <Table.Td><Badge color="blue">{formatDateString(project.getLastEditedDate())}</Badge></Table.Td>
                                    <Table.Td><Badge color={project.submissions.length <= 0 ? "red" : "darkGreen"}>{getLatestSubmissionDate(project)}</Badge></Table.Td>
                                    <Table.Td>
                                        <Button
                                            size="xs"
                                            onClick={() => Utils.openProject(project.getId())}
                                        >
                                            Load Project
                                        </Button>
                                    </Table.Td>
                                </Table.Tr>
                            ))}
                        </Table.Tbody>
                    </Table>
                </ScrollArea>
            )}
        </Card>
    );
};

const getLatestSubmissionDate = (project: Project): string => {
    if (!project.submissions || project.submissions.length === 0) {
        return "Not Submitted";
    }
    const latestSubmission = [...project.submissions].sort(
        (a, b) => new Date(b.dateSubmitted).getTime() - new Date(a.dateSubmitted).getTime()
    )[0];
    return formatDateString(latestSubmission.dateSubmitted);
};

// Defensive date parse: supports Date | string | null/undefined
const asDate = (d: Date | string | null | undefined): Date | null => {
    if (!d) return null;
    if (d instanceof Date) return isNaN(+d) ? null : d;
    // If backend sends UTC without 'Z', assume UTC
    const iso = /\dZ$/.test(d) || /T.*Z$/.test(d) ? d : `${d}Z`;
    const parsed = new Date(iso);
    return isNaN(+parsed) ? null : parsed;
};

const formatDateString = (d: Date | string | null | undefined): string =>
    asDate(d)?.toLocaleString() ?? "Invalid Date";

export default ClassroomProjectsTable;
